/**
 * Productization / feature licensing API.
 * Uses createApiConfig and fetch for GET /v1/features/accessible (returns accessible features + upgrade CTA).
 */

import { normalizeApiBaseUrl, getDefaultApiBaseUrl } from '@/lib/utils/url-normalization';
import { getCurrentAuthToken } from '@/lib/utils';

/** SHOW_ALL_DISABLE_PAID = show all integrations, disable install for PAID; SHOW_FREE_ONLY = only show FREE. */
export type MarketplaceVisibility = 'SHOW_ALL_DISABLE_PAID' | 'SHOW_FREE_ONLY';

export interface AccessibleFeaturesResponse {
  success: boolean;
  accessibleFeatureIds?: string[];
  features?: Array<{ featureId: string; displayName?: string; category?: string; defaultTier?: string }>;
  total?: number;
  upgradeCtaUrl?: string | null;
  upgradeCtaLabel?: string | null;
  upgradeCtaEmail?: string | null;
  marketplaceVisibility?: MarketplaceVisibility | string | null;
}

export interface UpgradeCta {
  url: string | null;
  label: string;
  email: string | null;
}

type AccessibleFeaturesCacheEntry = {
  expiresAt: number;
  value: AccessibleFeaturesResponse;
};

const ACCESSIBLE_FEATURES_TTL_MS = 60_000;
const accessibleFeaturesCache = new Map<string, AccessibleFeaturesCacheEntry>();
const accessibleFeaturesInFlight = new Map<string, Promise<AccessibleFeaturesResponse>>();

function toCacheKey(sessionId: string, tenantId?: string): string {
  return `${sessionId}::${tenantId && tenantId.trim() ? tenantId.trim() : '__no_tenant__'}`;
}

function getApiBaseUrl(): string {
  const base = getDefaultApiBaseUrl();
  const { apiBase } = normalizeApiBaseUrl(base || 'http://localhost:8080/api');
  return apiBase;
}

/**
 * Fetches accessible feature IDs and upgrade CTA for the current tenant.
 */
export async function getAccessibleFeatures(sessionId: string, tenantId?: string): Promise<AccessibleFeaturesResponse> {
  const cacheKey = toCacheKey(sessionId, tenantId);
  const now = Date.now();
  const cached = accessibleFeaturesCache.get(cacheKey);
  if (cached && cached.expiresAt > now) {
    return cached.value;
  }

  const inFlight = accessibleFeaturesInFlight.get(cacheKey);
  if (inFlight) {
    return inFlight;
  }

  const requestPromise = (async (): Promise<AccessibleFeaturesResponse> => {
  const apiBase = getApiBaseUrl();
  const url = new URL(`${apiBase}/v1/features/accessible`);
  url.searchParams.set('sessionId', sessionId);

  const headers: HeadersInit = { 'Content-Type': 'application/json' };
  const token = getCurrentAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(url.toString(), {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch accessible features: ${response.statusText}`);
  }

    const payload = (await response.json()) as AccessibleFeaturesResponse;
    accessibleFeaturesCache.set(cacheKey, {
      expiresAt: Date.now() + ACCESSIBLE_FEATURES_TTL_MS,
      value: payload,
    });
    return payload;
  })();

  accessibleFeaturesInFlight.set(cacheKey, requestPromise);
  try {
    return await requestPromise;
  } finally {
    accessibleFeaturesInFlight.delete(cacheKey);
  }
}

export function invalidateAccessibleFeaturesCache(
  scope?:
    | string
    | {
        sessionId?: string;
        tenantId?: string;
      }
): void {
  if (typeof scope === 'string') {
    const prefix = `${scope}::`;
    for (const key of Array.from(accessibleFeaturesCache.keys())) {
      if (key.startsWith(prefix)) {
        accessibleFeaturesCache.delete(key);
      }
    }
    for (const key of Array.from(accessibleFeaturesInFlight.keys())) {
      if (key.startsWith(prefix)) {
        accessibleFeaturesInFlight.delete(key);
      }
    }
    return;
  }
  if (scope?.sessionId) {
    if (scope.tenantId) {
      const key = toCacheKey(scope.sessionId, scope.tenantId);
      accessibleFeaturesCache.delete(key);
      accessibleFeaturesInFlight.delete(key);
      return;
    }
    const prefix = `${scope.sessionId}::`;
    for (const key of Array.from(accessibleFeaturesCache.keys())) {
      if (key.startsWith(prefix)) {
        accessibleFeaturesCache.delete(key);
      }
    }
    for (const key of Array.from(accessibleFeaturesInFlight.keys())) {
      if (key.startsWith(prefix)) {
        accessibleFeaturesInFlight.delete(key);
      }
    }
    return;
  }
  accessibleFeaturesCache.clear();
  accessibleFeaturesInFlight.clear();
}

/**
 * Parse upgrade CTA from API response (accessible or 403 error body).
 */
export function parseUpgradeCta(data: {
  upgradeCtaUrl?: string | null;
  upgradeCtaLabel?: string | null;
  upgradeCtaEmail?: string | null;
}): UpgradeCta {
  const label = data.upgradeCtaLabel && data.upgradeCtaLabel.trim() ? data.upgradeCtaLabel.trim() : 'Contact Sales';
  return {
    url: data.upgradeCtaUrl && data.upgradeCtaUrl.trim() ? data.upgradeCtaUrl.trim() : null,
    label,
    email: data.upgradeCtaEmail && data.upgradeCtaEmail.trim() ? data.upgradeCtaEmail.trim() : null,
  };
}

/**
 * Create a productization service instance (for use in hooks that need getAccessibleFeatures).
 */
export function createProductizationService() {
  return {
    getAccessibleFeatures,
  };
}
